import { useMemo } from "react"
import { useQuery } from "@tanstack/react-query"
import { resolveLocationNames, LocationNames } from "@/lib/name-resolver"
import { Language } from "@/lib/i18n"

export function useLocationNames(
  districtVal: string,
  blockVal: string,
  villageVal: string,
  lang: Language
) {
  const district = String(districtVal || "").trim()
  const block = String(blockVal || "").trim()
  const village = String(villageVal || "").trim()

  const fallbackNames = useMemo<LocationNames>(() => ({
    districtName: district,
    blockName: block,
    villageName: village,
  }), [block, district, village])

  const query = useQuery<LocationNames>({
    queryKey: ["location-names", lang, district, block, village],
    enabled: Boolean(district),
    staleTime: 60 * 60_000,
    gcTime: 60 * 60_000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
    retry: 1,
    queryFn: () => resolveLocationNames(district, block, village, lang),
  })

  const names = district ? (query.data ?? fallbackNames) : { districtName: "", blockName: "", villageName: "" }
  const error = query.error instanceof Error
    ? query.error
    : query.error
      ? new Error(String(query.error))
      : null
  const resolvedLang = query.data ? lang : undefined

  return {
    ...names,
    loading: Boolean(district) && query.isLoading,
    resolvedLang,
    error,
  }
}
